/**
 * Bit Depth hub (platform spec §4–5). One small router; views in ./views.
 * `?shell=launcher` switches to the launcher stack layout for the Tauri window.
 */

import "./styles/hub.css";
import { api, type Me } from "./api.js";
import { h, mount } from "./ui.js";
import { BUILD, CHANNEL } from "./version.generated.js";
import { loginView } from "./views/login.js";
import { homeView } from "./views/home.js";
import { createView } from "./views/create.js";
import { accountView } from "./views/account.js";
import { profileView, hiscoresView } from "./views/profile.js";
import { friendsView } from "./views/friends.js";
import { launcherView } from "./views/launcher.js";
import { shell } from "./shell.js";

export interface App {
  me: Me;
  refresh(): Promise<void>;
  go(path: string): void;
  build: string;
  channel: string;
  isLauncher: boolean;
}

const root = document.getElementById("app")!;
const isLauncher = shell.isLauncher;
if (isLauncher) document.body.classList.add("shell-launcher");
if (CHANNEL === "dev") document.body.classList.remove("crt-flicker");

const app: App = {
  me: { account: null },
  build: BUILD,
  channel: CHANNEL,
  isLauncher,
  async refresh() {
    try { app.me = await api.me(); } catch { app.me = { account: null }; }
  },
  go(path) {
    history.pushState(null, "", path);
    void render();
  },
};

window.addEventListener("popstate", () => void render());
document.addEventListener("click", (e) => {
  const a = (e.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;
  if (!a || a.target === "_blank" || a.origin !== location.origin || a.hasAttribute("download")) return;
  if (a.pathname.startsWith("/game/")) return; // the game is a real navigation
  e.preventDefault();
  app.go(a.pathname + a.search);
});

async function render(): Promise<void> {
  await app.refresh();
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const signedIn = !!app.me.account;

  if (!signedIn && !/^\/(profile\/|hiscores)/.test(path)) {
    mount(root, loginView(app));
    return;
  }
  if (signedIn && app.me.profile === null && !/^\/(create|account|profile\/|hiscores)/.test(path)) {
    // No character in the active era yet: create one before anything else.
    mount(root, frame(app, createView(app)));
    return;
  }
  if (isLauncher) {
    mount(root, launcherView(app, path));
    return;
  }
  let view: HTMLElement;
  if (path === "/" || path === "/home") view = homeView(app);
  else if (path === "/create") view = createView(app);
  else if (path === "/account") view = accountView(app);
  else if (path === "/friends") view = friendsView(app);
  else if (path === "/hiscores") view = hiscoresView(app);
  else if (path.startsWith("/profile/")) view = profileView(app, decodeURIComponent(path.slice("/profile/".length)));
  else view = h("div", { class: "panel" }, h("h2", {}, "Not found"), h("a", { href: "/home" }, "Back to home"));
  mount(root, frame(app, view, path));
}

export function frame(app: App, view: HTMLElement, path = location.pathname): HTMLElement {
  const nav = (href: string, label: string) => h("a", { href, class: path === href || (href !== "/home" && path.startsWith(href)) ? "active" : "" }, label);
  const handle = app.me.profile?.handle;
  return h("div", { class: "frame" },
    h("div", { class: "signal", "aria-hidden": "true" }),
    h("header", { class: "topbar" },
      h("a", { href: "/home", class: "logo" }, pixelMark(), "Bit Depth"),
      h("nav", {},
        nav("/home", "Home"),
        nav("/friends", "Friends"),
        nav("/hiscores", "Hiscores"),
        handle ? nav(`/profile/${handle}`, "Profile") : null,
        nav("/account", "Account"),
      ),
      h("span", { class: "badge" }, (app.me.era?.id ?? "pre_alpha").replace("_", "-")),
    ),
    view,
    h("footer", { class: "footer" },
      h("span", { class: "mono-num" }, `build ${app.build} · ${app.channel}`),
      h("span", {}, "© Psyrcuit LLC"),
    ),
  );
}

export function pixelMark(): HTMLElement {
  return h("span", { class: "px", "aria-hidden": "true" }, h("i"), h("i"), h("i"), h("i"));
}

void render();

if ("serviceWorker" in navigator && location.protocol === "https:") {
  navigator.serviceWorker.register("/sw.js").catch(() => { /* installability only */ });
}
